import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { usePondNames } from '@/features/shared/hooks/usePondNames';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/shared/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/Card';
import { ArrowLeft, Fish, AlertTriangle, Scale, Activity, Bug, Stethoscope, Image as ImageIcon, FileText, Thermometer, Cloud, Brain, Pencil } from 'lucide-react';
import { fishHealthApi } from '@/api/fishHealthApi';
import { LocationDisplay } from '@/shared/components/ui/LocationDisplay';
import type { FishHealthCheck } from '@/api/fishHealthApi';
import { imageApi } from '@/shared/api/imageApi';
import { ImageGallery } from '@/shared/components/ui/ImageGallery';
import { WeatherDisplay } from '@/shared/components/ui/WeatherDisplay';
import { useImageStore } from '@/shared/stores/imageStore';
import { queryKeys } from '@/shared/api/queryClient';
import { buildRoute } from '@/shared/lib/constants';
import { MetricBox, StatusBadge } from '@/features/shared/components/records';
import { useErrorToast } from '@/shared/hooks/useErrorToast';
import { format } from 'date-fns';

const riskColors = {
  low: 'success' as const,
  medium: 'warning' as const,
  high: 'error' as const,
  critical: 'error' as const,
};

export const FishHealthDetailPage: React.FC = () => {
  const { farmId, checkId } = useParams<{
    farmId: string;
    checkId: string;
  }>();
  const pondNames = usePondNames(farmId);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const pondIdFromUrl = searchParams.get('pondId');
  const { showAxiosError } = useErrorToast();
  const [check, setCheck] = useState<FishHealthCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const [showGallery, setShowGallery] = useState(false);
  const [galleryStartIndex, setGalleryStartIndex] = useState(0);

  // Zustand image store
  const setImages = useImageStore((state) => state.setImages);

  useEffect(() => {
    if (!farmId || !checkId) return;

    const loadCheck = async () => {
      try {
        setLoading(true);
        // Fetch from the list API and find the specific check
        const listResponse = await fishHealthApi.listChecks(farmId, { limit: 100 });
        const checkData = listResponse.data.find(c => c.id === checkId);
        if (!checkData) throw new Error('Check not found');
        setCheck(checkData);
      } catch (error) {
        showAxiosError(error);
      } finally {
        setLoading(false);
      }
    };

    loadCheck();
  }, [farmId, checkId, showAxiosError]);

  // Fetch signed URLs for images (same pattern as water quality)
  const { data: signedUrlsResponse, isLoading: isImagesLoading } = useQuery({
    queryKey: queryKeys.images.signedUrls(check?.imageIds || []),
    queryFn: () => imageApi.getSignedUrls(check?.imageIds || []),
    enabled: !!check?.imageIds && check.imageIds.length > 0,
  });

  // Update Zustand cache when signed URLs are fetched
  useEffect(() => {
    if (signedUrlsResponse?.images) {
      setImages(signedUrlsResponse.images);
    }
  }, [signedUrlsResponse, setImages]);

  const handleImageClick = (index: number) => {
    setGalleryStartIndex(index);
    setShowGallery(true);
  };

  if (loading) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  if (!check) {
    return <div className="p-8 text-center">Health check not found</div>;
  }

  // Calculate mortality rate if not provided
  const mortalityRate = check.mortalityRate ??
    (check.mortalityCount && check.sampleSize > 0
      ? ((check.mortalityCount / check.sampleSize) * 100)
      : 0);

  const hasParasite = check.parasiteCode && check.parasiteName;
  const hasImages = check.imageIds && check.imageIds.length > 0;
  const images = signedUrlsResponse?.images || [];

  return (
    <>
      <div className="p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => {
            if (farmId) {
              navigate(buildRoute.fishHealth(farmId, pondIdFromUrl));
            } else {
              navigate(-1);
            }
          }}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200/50">
              <Fish className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">
                {pondNames[check.pondId] || check.pond?.name || `Pond ${check.pondId.slice(0, 8)}`}
              </h1>
              <p className="text-sm text-slate-500">
                {format(new Date(check.checkedAt), 'PPpp')}
              </p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => navigate(buildRoute.fishHealthEdit(farmId!, checkId!, pondIdFromUrl))}
            >
              <Pencil className="h-4 w-4 mr-1" />
              Edit
            </Button>
            {check.riskLevel && (
              <StatusBadge variant={riskColors[check.riskLevel]}>
                {check.riskLevel.toUpperCase()} Risk
              </StatusBadge>
            )}
          </div>
        </div>

        {/* AI Diagnosis Recommendation Banner */}
        {check.riskLevel && ['medium', 'high', 'critical'].includes(check.riskLevel) && farmId && (
          <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200/50 rounded-lg shadow-sm">
            <div className="p-2.5 bg-amber-100 rounded-lg border border-amber-200/50">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-amber-900">AI Diagnosis Recommended</p>
              <p className="text-sm text-amber-700">
                This check shows {check.riskLevel} risk. Get AI-powered diagnosis and treatment recommendations.
              </p>
            </div>
            <Button
              onClick={() => navigate(buildRoute.aiDiagnosis(farmId, { healthCheckId: check.id, pondId: check.pondId }))}
              className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white"
            >
              <Brain className="h-4 w-4" />
              Get AI Diagnosis
            </Button>
          </div>
        )}

        {/* Health Score Banner */}
        {check.healthScore !== undefined && (
          <Card className={`border-l-4 ${
            check.healthScore >= 80 ? 'border-l-emerald-500 bg-emerald-50/50' :
            check.healthScore >= 60 ? 'border-l-amber-500 bg-amber-50/50' :
            'border-l-red-500 bg-red-50/50'
          }`}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600">Overall Health Score</p>
                  <p className="text-3xl font-bold">
                    {check.healthScore.toFixed(1)}
                    <span className="text-lg font-normal text-slate-400"> / 100</span>
                  </p>
                </div>
                {check.recommendedActions && check.recommendedActions.length > 0 && (
                  <div className="text-right">
                    <p className="text-sm text-slate-600 mb-1">Recommended Actions:</p>
                    <ul className="text-sm text-slate-700 list-disc list-inside">
                      {check.recommendedActions.slice(0, 3).map((action, i) => (
                        <li key={i}>{action}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <MetricBox
            label="Average Weight"
            value={`${check.averageWeight.toFixed(1)}g`}
            icon={Scale}
            color="blue"
          />
          <MetricBox
            label="Sample Size"
            value={`${check.sampleSize} fish`}
            icon={Fish}
            color="emerald"
          />
          {check.mortalityCount !== undefined && (
            <MetricBox
              label="Mortality"
              value={`${check.mortalityCount} fish`}
              icon={AlertTriangle}
              color={mortalityRate > 5 ? 'red' : 'emerald'}
              subtext={`${mortalityRate.toFixed(1)}% rate`}
            />
          )}
          {check.feedConversionRatio !== undefined && (
            <MetricBox
              label="FCR"
              value={check.feedConversionRatio.toFixed(2)}
              icon={Activity}
              color={check.feedConversionRatio < 1.5 ? 'emerald' : check.feedConversionRatio < 2.0 ? 'amber' : 'red'}
            />
          )}
        </div>

        {/* Sample Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-slate-600" />
              Sample Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-slate-500">Feed Type</p>
                <p className="font-semibold text-slate-900">{check.feedType}</p>
              </div>
              {check.tasteTestResult && (
                <div>
                  <p className="text-sm text-slate-500">Taste Test</p>
                  <p className="font-semibold text-slate-900">{check.tasteTestResult}</p>
                </div>
              )}
              {check.growthRate !== undefined && (
                <div>
                  <p className="text-sm text-slate-500">Growth Rate</p>
                  <p className="font-semibold text-slate-900">{check.growthRate.toFixed(2)}%</p>
                </div>
              )}
              {check.performedByName && (
                <div>
                  <p className="text-sm text-slate-500">Performed By</p>
                  <p className="font-semibold text-slate-900">{check.performedByName}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Environmental Conditions */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Cloud className="h-5 w-5 text-slate-600" />
              Environmental Conditions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Manual observations */}
            <div className="grid grid-cols-2 gap-4">
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <Thermometer className="h-5 w-5 text-blue-600" />
                <div>
                  <p className="text-sm text-slate-500">Water Temperature</p>
                  {check.waterTemperature != null && check.waterTemperature > 0 ? (
                    <p className="font-semibold text-slate-900">{check.waterTemperature}°C</p>
                  ) : (
                    <p className="text-slate-400 italic">Not recorded</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Cloud className="h-5 w-5 text-slate-600" />
                <div>
                  <p className="text-sm text-slate-500">Weather Notes</p>
                  {check.weatherConditions ? (
                    <p className="font-semibold text-slate-900">{check.weatherConditions}</p>
                  ) : (
                    <p className="text-slate-400 italic">Not recorded</p>
                  )}
                </div>
              </div>
            </div>

            {/* Automated Weather Data (from Open-Meteo BOM API) */}
            {check.weatherData && (
              <div className="pt-2">
                <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Automated Weather Data</p>
                <WeatherDisplay weather={check.weatherData} />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Parasite Detection */}
        {hasParasite && (
          <Card className="border-amber-200 bg-amber-50/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-amber-700">
                <Bug className="h-5 w-5" />
                Parasite Detected
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4 p-4 bg-white rounded-lg border border-amber-200">
                <div className="p-3 bg-amber-100 rounded-full">
                  <AlertTriangle className="h-6 w-6 text-amber-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-slate-900">{check.parasiteCode}</p>
                  <p className="text-slate-600">{check.parasiteName}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Health Observations */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-slate-600" />
              Health Observations
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {check.physicalSymptoms && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-semibold text-slate-600 mb-2">Physical Symptoms</p>
                <p className="text-slate-700">{check.physicalSymptoms}</p>
              </div>
            )}
            {check.behaviorObservations && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-semibold text-slate-600 mb-2">Behavior Observations</p>
                <p className="text-slate-700">{check.behaviorObservations}</p>
              </div>
            )}
            {check.observations && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm font-semibold text-slate-600 mb-2">General Observations</p>
                <p className="text-slate-700">{check.observations}</p>
              </div>
            )}
            {!check.physicalSymptoms && !check.behaviorObservations && !check.observations && (
              <p className="text-slate-400 italic">No observations recorded</p>
            )}
          </CardContent>
        </Card>

        {/* Treatment & Vet Notes */}
        {(check.treatmentApplied || check.veterinarianNotes) && (
          <Card className="border-emerald-200 bg-emerald-50/30">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-emerald-700">
                <Stethoscope className="h-5 w-5" />
                Treatment & Veterinary Notes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {check.treatmentApplied && (
                <div className="p-4 bg-white rounded-lg border border-emerald-200">
                  <p className="text-sm font-semibold text-emerald-700 mb-2">Treatment Applied</p>
                  <p className="text-slate-700">{check.treatmentApplied}</p>
                </div>
              )}
              {check.veterinarianNotes && (
                <div className="p-4 bg-white rounded-lg border border-emerald-200">
                  <p className="text-sm font-semibold text-emerald-700 mb-2">Veterinarian Notes</p>
                  <p className="text-slate-700">{check.veterinarianNotes}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Photos - using same pattern as water quality */}
        {hasImages && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <ImageIcon className="h-5 w-5 text-slate-600" />
                  Photos ({check.imageIds?.length})
                </CardTitle>
                {isImagesLoading && (
                  <span className="text-sm text-slate-500">Loading...</span>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {images.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {images.map((image, index) => (
                    <button
                      key={image.imageId}
                      onClick={() => handleImageClick(index)}
                      className="group relative aspect-square rounded-lg overflow-hidden bg-slate-100 hover:ring-2 hover:ring-emerald-500 transition-all"
                    >
                      <img
                        src={image.signedUrl}
                        alt={image.fileName}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <ImageIcon className="h-8 w-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">
                    {isImagesLoading ? "Loading images..." : "No images available"}
                  </p>
                </div>
              )}

              {signedUrlsResponse?.unauthorized && signedUrlsResponse.unauthorized.length > 0 && (
                <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                  <p className="text-sm text-yellow-800">
                    {signedUrlsResponse.unauthorized.length} image(s) could not be loaded due to permissions
                  </p>
                </div>
              )}

              {check.hasPendingImages && (
                <p className="text-sm text-amber-600 mt-3 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Some images are still pending upload
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Metadata */}
        <Card>
          <CardContent className="px-6 py-5">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6 text-sm">
              <div>
                <span className="text-slate-500">Performed By</span>
                <div className="text-slate-700 mt-0.5">
                  {check.performedByName || <span className="text-slate-400 italic">Unknown</span>}
                </div>
              </div>
              <div>
                <span className="text-slate-500">Checked</span>
                <div className="text-slate-700 mt-0.5">{format(new Date(check.checkedAt), 'PPpp')}</div>
              </div>
              <div>
                <span className="text-slate-500">Created</span>
                <div className="text-slate-700 mt-0.5">{format(new Date(check.createdAt), 'PPpp')}</div>
              </div>
              <div>
                <span className="text-slate-500">Check ID</span>
                <div className="font-mono text-slate-700 mt-0.5 text-xs break-all">{check.id}</div>
              </div>
              <div>
                <span className="text-slate-500">Location</span>
                <div className="mt-0.5">
                  <LocationDisplay latitude={check.latitude} longitude={check.longitude} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Image Gallery Lightbox */}
      {showGallery && images.length > 0 && (
        <ImageGallery
          images={images.map((img) => ({
            imageId: img.imageId,
            fileName: img.fileName,
            signedUrl: img.signedUrl,
          }))}
          initialIndex={galleryStartIndex}
          onClose={() => setShowGallery(false)}
        />
      )}
    </>
  );
};
